import React, { createContext, useContext, useState, useCallback } from 'react';
import { useAuth } from './authContext';

// =============================================================================
// QR Create admin context
// -----------------------------------------------------------------------------
// Data layer for the QR Create operator dashboard. Talks to the single
// qr_create_admin_context package on the Go backend, mounted under both /admin
// and /master (dual access, like the Navela and PR Local sections):
//
//   {API}/{admin|master}/qrcreate-admin/api/v1/qr-create/...
//
// What the backend exposes, and the shapes to expect:
//   • /overview            top-line rollup + scan and signup timelines
//   • /users               paginated; each row carries the user's credit
//                          balance, plan and code/scan counts already joined
//   • /users/{id}          the same row plus the credit ledger, the plan
//                          history and a scan rollup
//   • /codes, /scans       paginated explorers; /scans/analytics is the
//                          whole-product rollup, /scans/filters the dropdowns
//
// The two writes:
//   • POST /users/{id}/credits          {amount, bucket, note} — amount is
//     SIGNED (negative removes). Removals clamp at the floor rather than
//     failing, and the response reports {requested, applied, clamped, balance},
//     so the UI should tell the operator what actually happened rather than
//     assume the request went through as typed.
//   • PUT  /users/{id}/subscription     {plan, ...} — every field except plan is
//     optional and omitted fields keep their current value.
//
// Note on entitlements: `subscription.entitlements.pro` is the derived answer to
// "is this account pro right now" (an expired pro row is a free user, a canceled
// one is still pro until its period ends). Branch on that, never on `plan`.
// =============================================================================

const QRCreateContext = createContext();

export const useQRCreate = () => {
  const context = useContext(QRCreateContext);
  if (!context) {
    throw new Error('useQRCreate must be used within a QRCreateProvider');
  }
  return context;
};

export const QRCreateProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { makeAuthenticatedRequest, loginType } = useAuth();

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://admin-golang-backend-8cdf9b481f22.herokuapp.com';

  // The package is mounted behind both /admin and /master. Pick the scope for
  // the current login.
  const scope = useCallback(() => {
    if (loginType === 'master') return 'master';
    if (loginType === 'admin') return 'admin';
    throw new Error('Invalid login type for QR Create access');
  }, [loginType]);

  const base = useCallback(
    () => `${API_BASE_URL}/${scope()}/qrcreate-admin/api/v1/qr-create`,
    [API_BASE_URL, scope],
  );

  const validateAccess = useCallback(() => {
    if (loginType !== 'admin' && loginType !== 'master') {
      throw new Error('Only admin and master users can manage QR Create data');
    }
  }, [loginType]);

  const handleApiResponse = async (response) => {
    if (response.status === 204) {
      if (!response.ok) throw new Error('Request failed');
      return null;
    }
    let data = null;
    const text = await response.text();
    if (text) {
      try {
        data = JSON.parse(text);
      } catch {
        data = { raw: text };
      }
    }
    if (!response.ok) {
      const msg = (data && (data.error || data.message)) || 'Request failed';
      const err = new Error(msg);
      err.status = response.status;
      err.data = data;
      throw err;
    }
    return data;
  };

  const clearError = useCallback(() => setError(null), []);

  // Wrap loading/error handling around one request so each helper stays tiny.
  const run = useCallback(async (fn, fallbackMsg) => {
    validateAccess();
    setLoading(true);
    setError(null);
    try {
      return await fn();
    } catch (err) {
      const msg = err.message || fallbackMsg;
      setError(msg);
      const wrapped = new Error(msg);
      wrapped.status = err.status;
      throw wrapped;
    } finally {
      setLoading(false);
    }
  }, [validateAccess]);

  const buildQuery = (options = {}, keys = []) => {
    const params = new URLSearchParams();
    keys.forEach((k) => {
      const v = options[k];
      if (v !== undefined && v !== null && v !== '') params.append(k, v);
    });
    const qs = params.toString();
    return qs ? `?${qs}` : '';
  };

  const jsonBody = (payload) => ({
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  const get = useCallback((path, message) => run(async () =>
    handleApiResponse(await makeAuthenticatedRequest(`${base()}${path}`)),
  message), [run, makeAuthenticatedRequest, base]);

  // ── Overview ───────────────────────────────────────────────────────────────
  const fetchOverview = useCallback((options = {}) =>
    get(`/overview${buildQuery(options, ['days'])}`, 'Failed to load the QR Create overview'),
  [get]);

  // ── Users ──────────────────────────────────────────────────────────────────
  const fetchUsers = useCallback((options = {}) =>
    get(`/users${buildQuery(options, ['search', 'plan', 'status', 'entitled', 'sort', 'order', 'limit', 'offset'])}`,
      'Failed to load registered users'),
  [get]);

  const getUser = useCallback((userId, options = {}) =>
    get(`/users/${encodeURIComponent(userId)}${buildQuery(options, ['days', 'limit'])}`, 'Failed to load the user'),
  [get]);

  const fetchUserCodes = useCallback((userId, options = {}) =>
    get(`/users/${encodeURIComponent(userId)}/codes${buildQuery(options, ['search', 'qr_type', 'customized', 'active', 'sort', 'order', 'limit', 'offset'])}`,
      "Failed to load the user's codes"),
  [get]);

  const fetchUserCredits = useCallback((userId, options = {}) =>
    get(`/users/${encodeURIComponent(userId)}/credits${buildQuery(options, ['limit'])}`, 'Failed to load the credit ledger'),
  [get]);

  const fetchUserScans = useCallback((userId, options = {}) =>
    get(`/users/${encodeURIComponent(userId)}/scans${buildQuery(options, ['days'])}`, "Failed to load the user's scan analytics"),
  [get]);

  const fetchUserSubscription = useCallback((userId, options = {}) =>
    get(`/users/${encodeURIComponent(userId)}/subscription${buildQuery(options, ['limit'])}`, 'Failed to load the subscription'),
  [get]);

  // ── Writes ─────────────────────────────────────────────────────────────────

  // amount is signed: positive adds, negative removes. The result reports what
  // was actually applied, which can be smaller than requested when a removal hit
  // the floor — surface `applied` and `clamped` rather than echoing the request.
  const adjustCredits = useCallback((userId, { amount, bucket = 'credit', note = '' }) => run(async () =>
    handleApiResponse(await makeAuthenticatedRequest(
      `${base()}/users/${encodeURIComponent(userId)}/credits`,
      { method: 'POST', ...jsonBody({ amount, bucket, note }) },
    )),
  'Failed to adjust credits'), [run, makeAuthenticatedRequest, base]);

  // Omitted fields keep their current value, so a partial payload is fine —
  // only `plan` is required.
  const updateSubscription = useCallback((userId, payload) => run(async () =>
    handleApiResponse(await makeAuthenticatedRequest(
      `${base()}/users/${encodeURIComponent(userId)}/subscription`,
      { method: 'PUT', ...jsonBody(payload) },
    )),
  'Failed to update the subscription'), [run, makeAuthenticatedRequest, base]);

  // ── Codes ──────────────────────────────────────────────────────────────────
  const fetchCodes = useCallback((options = {}) =>
    get(`/codes${buildQuery(options, ['search', 'user_id', 'qr_type', 'customized', 'active', 'sort', 'order', 'limit', 'offset'])}`,
      'Failed to load generated codes'),
  [get]);

  const getCode = useCallback((generationId) =>
    get(`/codes/${encodeURIComponent(generationId)}`, 'Failed to load the code'),
  [get]);

  const fetchCodeScans = useCallback((generationId, options = {}) =>
    get(`/codes/${encodeURIComponent(generationId)}/scans${buildQuery(options, ['days'])}`,
      "Failed to load the code's scan analytics"),
  [get]);

  // ── Scans ──────────────────────────────────────────────────────────────────
  const fetchScanAnalytics = useCallback((options = {}) =>
    get(`/scans/analytics${buildQuery(options, ['days'])}`, 'Failed to load scan analytics'),
  [get]);

  const fetchScans = useCallback((options = {}) =>
    get(`/scans${buildQuery(options, ['user_id', 'generation_id', 'country', 'device_type', 'route', 'days', 'limit', 'offset'])}`,
      'Failed to load the scan feed'),
  [get]);

  const fetchScanFilters = useCallback(() =>
    get('/scans/filters', 'Failed to load scan filter options'),
  [get]);

  // ── Catalog ────────────────────────────────────────────────────────────────
  const fetchCreditPacks = useCallback(() =>
    get('/credit-packs', 'Failed to load credit packs'),
  [get]);

  const value = {
    loading,
    error,
    clearError,
    // overview
    fetchOverview,
    // users
    fetchUsers, getUser, fetchUserCodes, fetchUserCredits, fetchUserScans, fetchUserSubscription,
    // writes
    adjustCredits, updateSubscription,
    // codes
    fetchCodes, getCode, fetchCodeScans,
    // scans
    fetchScanAnalytics, fetchScans, fetchScanFilters,
    // catalog
    fetchCreditPacks,
    // access flags
    isAdminUser: loginType === 'admin',
    isMasterUser: loginType === 'master',
    hasQRCreateAccess: loginType === 'admin' || loginType === 'master',
  };

  return (
    <QRCreateContext.Provider value={value}>
      {children}
    </QRCreateContext.Provider>
  );
};
